import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { apiDelete, apiGet } from '../api';
import { DashboardLayout } from '../components/Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Star, Trash2, Loader2, MapPin } from 'lucide-react';
import { toast } from 'sonner';
import { formatApiError } from '../lib/apiError';

/**
 * FavouritesPage — Bug #10 fix (Iter-59).
 *
 * A dedicated /favourites screen so clients can jump straight to their
 * saved studios from the sidebar quick-nav. Each card is fully clickable
 * (the whole tile links to /studio/:slug — the previous ProfilePage
 * treatment only wrapped the studio name in a <Link>, so most of the row
 * was "unclickable" per the reported bug).
 */
export const FavouritesPage = () => {
  const [favourites, setFavourites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [removingId, setRemovingId] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        const data = await apiGet('/api/favourites');
        setFavourites(Array.isArray(data) ? data : []);
      } catch (e) {
        toast.error(formatApiError(e, 'Failed to load favourites'));
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const handleRemove = async (e, studioId) => {
    // Prevent the wrapping Link from firing when the trash icon is clicked.
    e.preventDefault();
    e.stopPropagation();
    setRemovingId(studioId);
    try {
      await apiDelete('/api/favourites/{studio_id}', { params: { studio_id: studioId } });
      setFavourites((prev) => prev.filter((f) => f.id !== studioId));
      toast.success('Removed from favourites');
    } catch (err) {
      toast.error(formatApiError(err, 'Failed to remove favourite'));
    } finally {
      setRemovingId(null);
    }
  };

  return (
    <DashboardLayout>
      <div className="max-w-5xl mx-auto space-y-6" data-testid="favourites-page">
        <div>
          <h1 className="text-3xl font-bold mb-2">Favourite Studios</h1>
          <p className="text-muted-foreground">
            Studios you&apos;ve saved for quick access. Tap any tile to jump straight to its schedule.
          </p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Star className="w-5 h-5 text-yellow-500" />
              Your Favourites ({favourites.length})
            </CardTitle>
            <CardDescription>Click a studio to view its schedule, ClassPasses and reviews.</CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
              </div>
            ) : favourites.length === 0 ? (
              <div className="text-center py-8">
                <Star className="w-12 h-12 mx-auto text-muted-foreground/50 mb-3" />
                <p className="text-muted-foreground mb-4">No favourite studios yet</p>
                <Link to="/studios">
                  <Button variant="outline" data-testid="browse-studios-empty-btn">Browse Studios</Button>
                </Link>
              </div>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {favourites.map((studio) => (
                  <Link
                    key={studio.id}
                    to={`/studio/${studio.slug || studio.id}`}
                    className="group relative flex items-start gap-3 p-4 rounded-lg border hover:border-primary hover:shadow-md transition-all bg-card"
                    data-testid={`favourite-tile-${studio.id}`}
                  >
                    {studio.logo_url ? (
                      <img
                        src={studio.logo_url.startsWith('/api') ? `${process.env.REACT_APP_BACKEND_URL}${studio.logo_url}` : studio.logo_url}
                        alt={studio.name}
                        className="w-12 h-12 rounded-lg object-cover flex-shrink-0"
                      />
                    ) : (
                      <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                        <span className="text-primary font-bold text-lg">{studio.name?.[0] || '?'}</span>
                      </div>
                    )}
                    <div className="flex-1 min-w-0 pr-8">
                      <p className="font-semibold group-hover:text-primary transition-colors truncate">{studio.name}</p>
                      {studio.city && (
                        <p className="text-xs text-muted-foreground flex items-center gap-1 mt-1">
                          <MapPin className="w-3 h-3" />
                          <span className="truncate">{studio.city}</span>
                        </p>
                      )}
                      {studio.rating != null && (
                        <div className="flex items-center gap-1 text-sm text-muted-foreground mt-1">
                          <Star className="w-3 h-3 fill-yellow-400 text-yellow-400" />
                          {Number(studio.rating).toFixed(1)}
                        </div>
                      )}
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={(e) => handleRemove(e, studio.id)}
                      disabled={removingId === studio.id}
                      className="absolute top-2 right-2 h-8 w-8 text-muted-foreground hover:text-destructive"
                      aria-label={`Remove ${studio.name} from favourites`}
                      data-testid={`remove-favourite-${studio.id}`}
                    >
                      {removingId === studio.id ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Trash2 className="w-4 h-4" />
                      )}
                    </Button>
                  </Link>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default FavouritesPage;
